import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, UserPlus, FileText, ShieldCheck, Activity } from "lucide-react";
import { fadeUp } from "./anim";
import api from "@/lib/api";

const KIND_ICON = {
  search: Search,
  assessment: ShieldCheck,
  host_joined: UserPlus,
  report: FileText,
};

// Minimum number of GENUINE recent events required to fill the card with real
// activity. Below this we fall back to clearly-generic example content so the
// section always looks active (without ever claiming examples are live users).
const MIN_GENUINE = 3;
const SHOW_COUNT = 4; // entries visible in the card (kept within the 3–5 range)

// Clearly-generic, privacy-safe example activity. NO names, locations, booking
// or property details — nothing implies a specific real user did something.
const FALLBACK_POOL = [
  { kind: "assessment", message: "Risk assessment completed" },
  { kind: "report", message: "Host reviewed a risk report" },
  { kind: "search", message: "Booking risk assessment started" },
  { kind: "assessment", message: "Host completed an assessment" },
  { kind: "report", message: "Evidence report generated" },
  { kind: "search", message: "Booking risk check in progress" },
  { kind: "assessment", message: "Risk score calculated for a booking" },
  { kind: "report", message: "Host downloaded an evidence report" },
];

// Presentational-only relative labels used for fallback examples. These are NOT
// real timestamps and the honest heading makes clear this is example content.
const FALLBACK_LABELS = ["Just now", "3 min ago", "12 min ago", "27 min ago", "41 min ago"];

const relTime = (iso) => {
  if (!iso) return "Just now";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diff < 60) return "Just now";
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "Yesterday";
  if (d <= 6) return `${d} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

// Build a rotating set of fallback entries. `offset` rotates which examples show
// so the card visibly refreshes over time.
const buildFallback = (offset) => {
  const out = [];
  for (let i = 0; i < SHOW_COUNT; i += 1) {
    const src = FALLBACK_POOL[(offset + i) % FALLBACK_POOL.length];
    out.push({
      kind: src.kind,
      message: src.message,
      timeLabel: FALLBACK_LABELS[i] || `${i * 10} min ago`,
      fallback: true,
    });
  }
  return out;
};

export const LiveActivity = () => {
  const [genuine, setGenuine] = useState([]);
  const [fallbackOffset, setFallbackOffset] = useState(() => Math.floor(Math.random() * FALLBACK_POOL.length));

  const load = () =>
    api
      .get("/activity/live")
      .then((r) => setGenuine(Array.isArray(r.data?.activity) ? r.data.activity : []))
      .catch(() => { /* keep whatever we have; never hide on error */ });

  // Poll genuine activity so real events replace examples automatically.
  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, []);

  // Rotate the fallback examples periodically so the section always looks fresh.
  useEffect(() => {
    const t = setInterval(() => setFallbackOffset((o) => (o + SHOW_COUNT) % FALLBACK_POOL.length), 45000);
    return () => clearInterval(t);
  }, []);

  // Decide what to display. Use genuine activity when there is enough of it;
  // otherwise show rotating generic examples. The section ALWAYS renders.
  const usingGenuine = genuine.length >= MIN_GENUINE;
  const displayItems = useMemo(() => {
    if (usingGenuine) {
      return genuine.slice(0, 5).map((a) => ({
        kind: a.kind,
        message: a.message,
        timeLabel: relTime(a.created_at),
        fallback: false,
      }));
    }
    return buildFallback(fallbackOffset);
  }, [usingGenuine, genuine, fallbackOffset]);

  return (
    <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--risk-low))]/25 bg-[hsl(var(--risk-low-bg))] px-3 py-1 text-xs font-medium text-[hsl(var(--risk-low))]">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--risk-low))] opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--risk-low))]" />
          </span>
          Live
        </span>
        <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight" data-testid="live-activity-heading">Live HostGuard activity</h2>
        <p className="mt-3 text-muted-foreground" data-testid="live-activity-desc">
          {usingGenuine
            ? "Real, privacy-safe platform activity \u2014 updating automatically."
            : "See how HostGuard helps hosts assess bookings."}
        </p>
      </motion.div>

      <motion.div {...fadeUp} className="mx-auto mt-8 max-w-xl overflow-hidden rounded-2xl border bg-card shadow-[0_10px_30px_rgba(15,23,42,0.05)]" data-testid="live-activity-feed">
        <div className="flex items-center gap-2 border-b px-5 py-3 text-xs font-medium text-muted-foreground">
          <Activity className="h-3.5 w-3.5 text-primary" /> Recent activity
        </div>
        <ul className="divide-y">
          <AnimatePresence initial={false} mode="popLayout">
            {displayItems.map((a, i) => {
              const Icon = KIND_ICON[a.kind] || Activity;
              return (
                <motion.li
                  key={`${a.fallback ? "ex" : "real"}-${a.message}-${i}`}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="flex items-center gap-3 px-5 py-3"
                  data-testid="live-activity-item"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--risk-low-bg))] text-[hsl(var(--risk-low))]">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">{a.message}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{a.timeLabel}</span>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      </motion.div>
    </section>
  );
};
